import React, { useState } from 'react';
import {
  Star, Bell, Cloud, Server, Plug, BookOpen, Github, Gitlab, Box,
  Database, Settings as SettingsIcon, X as XIcon, Loader2,
} from 'lucide-react';
import { useSnagcartSettings } from '../../hooks/useSnagcartSettings';
import {
  FeaturesSection, NotificationsSection, CloudProvidersSection, LocalProvidersSection,
  MCPServersSection, ProjectMemorySection, ServiceSection, DataManagementSection,
} from './SettingsSections';

const NAV = [
  { group: 'General', items: [
    { id: 'features',      label: 'Features',      icon: Star },
    { id: 'notifications', label: 'Notifications', icon: Bell },
  ]},
  { group: 'AI', items: [
    { id: 'cloud',    label: 'Cloud Providers', icon: Cloud },
    { id: 'local',    label: 'Local Providers', icon: Server, badge: 'BETA' },
    { id: 'mcp',      label: 'MCP Servers',     icon: Plug,   badge: 'BETA' },
    { id: 'memory',   label: 'Project Memory',  icon: BookOpen },
  ]},
  { group: 'Services', items: [
    { id: 'github',   label: 'GitHub',   icon: Github },
    { id: 'gitlab',   label: 'GitLab',   icon: Gitlab },
    { id: 'netlify',  label: 'Netlify',  icon: Cloud },
    { id: 'vercel',   label: 'Vercel',   icon: Cloud },
    { id: 'supabase', label: 'Supabase', icon: Box },
  ]},
  { group: 'System', items: [
    { id: 'data', label: 'Data Management', icon: Database },
  ]},
];

/**
 * SettingsModal — Devonz-style full-screen settings dialog with sidebar nav.
 * Visible sections: Features (Core/Beta/Prompt Library), Notifications,
 * Cloud/Local Providers, MCP Servers, Project Memory, GitHub/GitLab/Netlify/
 * Vercel/Supabase, Data Management.
 */
export default function SettingsModal({ open, onClose, onWiped }) {
  const [section, setSection] = useState('features');
  const { settings, loading, saving, save } = useSnagcartSettings();

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4 md:p-6"
      onClick={onClose}
      data-testid="settings-modal-overlay"
    >
      <div
        className="w-full max-w-6xl h-[90vh] bg-[#0a0f15] border border-white/[0.08] rounded-2xl shadow-2xl flex overflow-hidden"
        onClick={(e) => e.stopPropagation()}
        data-testid="settings-modal"
      >
        {/* Sidebar */}
        <aside className="w-60 shrink-0 border-r border-white/[0.06] bg-[#070b0f] flex flex-col">
          <div className="px-5 py-4 border-b border-white/[0.06] flex items-center gap-2">
            <SettingsIcon className="w-4 h-4 text-zinc-400" />
            <span className="text-lg font-semibold text-white">Settings</span>
            {(saving || loading) && <Loader2 className="w-3.5 h-3.5 ml-auto text-zinc-500 animate-spin" />}
          </div>
          <nav className="flex-1 overflow-y-auto py-3">
            {NAV.map((group) => (
              <div key={group.group} className="mb-3">
                <div className="px-5 py-2 text-[10px] uppercase tracking-wider text-zinc-600 font-semibold flex items-center gap-2">
                  <SettingsIcon className="w-3 h-3" /> {group.group}
                </div>
                {group.items.map((it) => {
                  const Icon = it.icon;
                  const active = section === it.id;
                  return (
                    <button
                      key={it.id}
                      onClick={() => setSection(it.id)}
                      data-testid={`settings-nav-${it.id}`}
                      className={`w-full flex items-center gap-3 px-5 py-2 text-sm transition-colors ${
                        active
                          ? 'text-cyan-300 bg-cyan-500/10 border-l-2 border-cyan-400 pl-[18px]'
                          : 'text-zinc-400 hover:text-zinc-200 hover:bg-white/[0.03] border-l-2 border-transparent'
                      }`}
                    >
                      <Icon className="w-3.5 h-3.5" />
                      <span className="flex-1 text-left">{it.label}</span>
                      {it.badge && (
                        <span className="text-[9px] font-semibold tracking-wide text-cyan-300 bg-cyan-500/15 border border-cyan-500/30 px-1.5 py-0.5 rounded">
                          {it.badge}
                        </span>
                      )}
                    </button>
                  );
                })}
              </div>
            ))}
          </nav>
        </aside>

        {/* Main content */}
        <main className="flex-1 flex flex-col min-w-0">
          <header className="flex items-center justify-between px-8 py-5 border-b border-white/[0.06]">
            <h2 className="text-base font-semibold text-white capitalize">
              {section === 'mcp' ? 'MCP Servers' : section === 'cloud' ? 'Cloud Providers' : section === 'local' ? 'Local Providers' : section === 'memory' ? 'Project Memory' : section === 'data' ? 'Data Management' : section}
            </h2>
            <button onClick={onClose} data-testid="settings-close-btn"
              className="p-2 rounded-lg hover:bg-white/[0.06] transition-colors text-zinc-400 hover:text-white">
              <XIcon className="w-4 h-4" />
            </button>
          </header>
          <div className="flex-1 overflow-y-auto px-8 py-6">
            {loading ? (
              <div className="text-center py-16 text-sm text-zinc-500 flex items-center justify-center gap-2">
                <Loader2 className="w-4 h-4 animate-spin" /> Loading settings…
              </div>
            ) : (
              <>
                {section === 'features'      && <FeaturesSection      settings={settings} save={save} />}
                {section === 'notifications' && <NotificationsSection settings={settings} save={save} />}
                {section === 'cloud'         && <CloudProvidersSection settings={settings} save={save} />}
                {section === 'local'         && <LocalProvidersSection />}
                {section === 'mcp'           && <MCPServersSection    settings={settings} save={save} />}
                {section === 'memory'        && <ProjectMemorySection settings={settings} save={save} />}
                {['github','gitlab','netlify','vercel','supabase'].includes(section) && (
                  <ServiceSection provKey={section} settings={settings} save={save} />
                )}
                {section === 'data'          && <DataManagementSection onWiped={onWiped} />}
              </>
            )}
          </div>
        </main>
      </div>
    </div>
  );
}
